import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { trustedBy } from '../content/data';

const TrustedBy = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Auto-rotate every 2 seconds when not hovered
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % trustedBy.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isHovered]);

  // Get current logos to display - 2 on mobile, 5 on desktop
  const getCurrentLogos = () => {
    const logos = [];
    const logoCount = isMobile ? 2 : 5; // 2 for mobile, 5 for desktop
    for (let i = 0; i < logoCount; i++) {
      const index = (currentIndex + i) % trustedBy.length;
      logos.push(trustedBy[index]);
    }
    return logos;
  };

  return (
    <section className="py-8 bg-gray-50 dark:bg-gray-900/50">
      <div className="container-max">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-6"
        >
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Selected projects and affiliations
          </p>
        </motion.div>

        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className={`grid gap-4 sm:gap-6 items-center ${
              isMobile 
                ? 'grid-cols-2' 
                : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
            }`}
          >
            {getCurrentLogos().map((company, index) => (
              <motion.a
                key={`${company.id}-${currentIndex}`}
                href={company.url}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05, y: -2 }}
                className="flex items-center justify-center p-2 sm:p-4 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-200"
              >
                <div className="w-24 h-16 sm:w-32 sm:h-20 flex items-center justify-center">
                  <img 
                    src={company.logoUrl} 
                    alt={`${company.name} logo`}
                    className="max-w-full max-h-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
                    onError={(e) => {
                      // Fallback to text if logo doesn't load
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<span class="text-xs font-medium text-gray-500 dark:text-gray-400 text-center">${company.name}</span>`;
                      }
                    }}
                  />
                </div>
              </motion.a>
            ))}
          </motion.div>

          {/* Carousel indicators */}
          <div className="flex justify-center mt-4 sm:mt-6 space-x-2">
            {trustedBy.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-colors duration-200 ${
                  index === currentIndex 
                    ? 'bg-accent-crimson' 
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedBy;
